import { X } from 'lucide-react';
import { buildLegend, type LegendEntry } from '../legend';
import { UNASSIGNED_DOT_CLASS, type MemberView } from '../members';

/**
 * The dismissible glossary panel behind the "?" chip (usability testing 2026-08-01 —
 * "what does orange mean?"). Every sample is rendered with the same utility classes the
 * grid uses, so it can never drift away from what's actually on screen.
 */
export function CalendarLegend({
  members,
  onClose,
}: {
  members: readonly MemberView[];
  onClose: () => void;
}): JSX.Element {
  const entries = buildLegend(members);
  return (
    <section
      data-testid="calendar-legend"
      aria-label="What the marks mean"
      className="flex flex-col gap-2 rounded-sm border border-line bg-surface p-3"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-[13px] font-semibold text-fg">What the marks mean</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Hide the legend"
          data-testid="calendar-legend-close"
          className="flex h-11 w-11 items-center justify-center text-fg-2"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <ul className="flex flex-col gap-1.5">
        {entries.map((entry) => (
          <li
            key={entry.id}
            data-testid="calendar-legend-entry"
            data-legend-id={entry.id}
            className="flex items-start gap-2.5"
          >
            <span className="flex h-5 w-6 shrink-0 items-center justify-center">
              <LegendSampleMark entry={entry} />
            </span>
            <span className="min-w-0 text-[12px] leading-snug">
              <span className="font-semibold text-fg">{entry.label}</span>
              <span className="text-fg-3"> — {entry.meaning}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LegendSampleMark({ entry }: { entry: LegendEntry }): JSX.Element {
  switch (entry.sample) {
    case 'hollow-dot':
      return (
        <span
          aria-hidden="true"
          className={`h-[9px] w-[9px] rounded-full ${UNASSIGNED_DOT_CLASS}`}
        />
      );
    case 'dot':
      return (
        <span
          aria-hidden="true"
          className="h-[9px] w-[9px] rounded-full"
          style={entry.style?.dot ?? { backgroundColor: 'var(--app-fg-3)' }}
        />
      );
    case 'birthday':
      return (
        <span aria-hidden="true" className="h-[9px] w-[9px] rounded-full border-2 border-accent" />
      );
    case 'today':
      return (
        <span
          aria-hidden="true"
          className="flex h-[22px] w-[22px] items-center justify-center rounded-full bg-inverse text-[11px] font-bold text-inverse-fg"
        >
          1
        </span>
      );
    case 'now-line':
      return <span aria-hidden="true" className="h-0.5 w-5 rounded-sm bg-accent" />;
    case 'more':
      return (
        <span aria-hidden="true" className="font-mono text-[10px] font-bold text-fg-3">
          +2
        </span>
      );
  }
}
